import React, { useState, useEffect, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import { useAuth } from "../context/authContext";
import {
  SearchGlassIcon,
  VerticalTwoBarsIcon,
  TrafficLightIcon,
} from "../icons/index";
import BlinkingSidewaysRightTriangleIcon from "../blinkingLight/blinkingSidewaysRightTriangleIcon";
import { memberCategoriesChoice } from "../../constants/categories";
import { InsetBlendTVButton, SquareFlatBtnsUpload } from "../buttons";
import {
  getPresignedVideoUrl,
  apiRecordVideoDataDatabase,
} from "../../api/backend/user";
import ErrorMessageModal from "../errorMessageModal/errorMessageModal";
import { uploadFileToS3 } from "../../api/backend/uploadToS3";
import { getVideoFileType } from "../../constants/fileUpload";
import { checkVideoFile } from "../../utils/checkVideoFile";
import VideoMonitor from "../videoPlayer/videoMonitor";
import VideoPlayerControls from "../videoPlayer/videoPlayerControls";
import Loader from "../loader/loader";
import { formatDuration } from "../../utils/formatTime";
import "./uploadVideoForm.scss";

const UploadVideoForm = ({
  isUploadVideoFormOpen, // state prop passed in for the remote to allow users to upload a skit
  closeModal, // state used to pass back to the parent pages to close the modal such as after uploading skit successfully also used for the remote to allow users to cancel which will close the modal
  currentMemberId, // this will be used to define the source user id to the current user id
  onServerSuccess, // this parameter will be used as a function below passed from the skits listing component and when it is invoked here the function will run in the skitst listing page or wherever else it is called back
}) => {
  console.log("At render, currentMemberId is", currentMemberId);
  const { currentMemberData } = useAuth();
  const [userHasPermissions, setUserHasPermissions] = useState(false);
  const [showError, setShowError] = useState(false);
  const [titleError, setTitleError] = useState(false);
  const [titleLengthError, setTitleLengthError] = useState(false);
  const [isSkitPlaying, setIsSkitPlaying] = useState(false);
  const [fileError, setFileError] = useState(false);
  const [fileTypeErrorMessage, setFileTypeErrorMessage] = useState("");
  const [categoryErr, setCategoryErr] = useState(false);
  const [durationError, setDurationError] = useState("");
  const [titleValue, setTitleValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [fileUpload, setFileUpload] = useState();
  const [BackendErrorMessage, setBackendErrorMessage] = useState(""); // Add state for error message
  const [presignedVideoUrlResponse, setPresignedVideoUrlResponse] =
    useState(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState("");
  const [previewVideoModal, setPreviewVideoModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState({
    id: "",
    name: "",
  });
  const [loading, setLoading] = useState(false);
  const [videoMetadata, setVideoMetadata] = useState({
    duration: 0,
    width: 0,
    height: 0,
  });
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0);
  const fileInputRef = useRef(null); // Ref for the hidden file input
  const videoRef = useRef(null); // Create a ref for the video element
  const modalRef = useRef(null); // Ref for the modal container used so scrolling
  const triggerFileInputClick = () => {
    fileInputRef.current.click(); // Programmatically click the hidden file input
  };
  // inside UploadVideoForm (top with other hooks)
  const STEPS = { COMPLIANCE: "compliance", UPLOAD: "upload" };
  const [step, setStep] = useState(STEPS.COMPLIANCE);

  const [agree, setAgree] = useState(false);
  const [flags, setFlags] = useState({
    puppets: false,
    cartoons: false,
    ai: false,
    vfx: false,
  });
  const isAnimation = flags.puppets || flags.cartoons || flags.ai || flags.vfx;

  const [isCategoryLocked, setIsCategoryLocked] = useState(false);

  // when leaving compliance: lock to Animations (id 200) if flagged
  const continueFromCompliance = () => {
    if (isAnimation) {
      const anim = memberCategoriesChoice.find((c) => c.id === 200);
      if (anim) setSelectedCategory({ id: anim.id, name: anim.name });
      setIsCategoryLocked(true);
    }
    setStep(STEPS.UPLOAD);
  };

  useEffect(() => {
    console.log("UploadVideoForm: currentMemberId is", currentMemberId);
    // Use currentMemberId as sourceId
  }, [currentMemberId]);

  useEffect(() => {
    const handleScroll = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        e.stopPropagation(); // Stop scroll propagation if inside the modal
      }
    };

    document.addEventListener("scroll", handleScroll, true);

    return () => {
      document.removeEventListener("scroll", handleScroll, true);
    };
  }, []);

  // this function will handle file size and type allowed
  const handleUploadVideo = async (e) => {
    console.log("handleUploadVideo: event triggered");
    const file = e?.target?.files[0];
    if (!file) {
      console.log("No file selected");
      return;
    }
    // Clear previous error messages and reset state
    setBackendErrorMessage("");
    setFileTypeErrorMessage("");
    setFileError(false);
    setDurationError("");
    getVideoFileType(file); // Determines if the file is 'mp4', 'mov', etc.
    console.log("Selected file:", file);
    console.log("File MIME type:", file.type);
    console.log("File size:", file.size);
    // Reset state values for video metadata
    setVideoMetadata({
      duration: 0,
      width: 0,
      height: 0,
    });
    // Perform file validation
    try {
      checkVideoFile(file.name, file.type, file.size); // Update this function for video validation
      // Create a video element to load metadata
      const videoElement = document.createElement("video");
      videoElement.preload = "metadata";
      videoElement.src = URL.createObjectURL(file); // Assuming file is your video file
      // Add event listener for 'loadedmetadata' event
      videoElement.addEventListener("loadedmetadata", () => {
        console.log("Loaded metadata for video, preparing preview...");
        // Extract metadata from video
        const duration = videoElement.duration;
        const width = videoElement.videoWidth;
        const height = videoElement.videoHeight;
        const durationInMinutes = (duration / 60).toFixed(2);
        // Update state with video metadata
        setVideoMetadata({ duration, width, height, durationInMinutes });
        // Perform duration check
        if (duration < 1 || duration > 1200) {
          console
            .error
            // "Video duration needs to be between 2 to 5 minutes long."
            ();
          setDurationError(
            "Video duration needs to be between 2 to 5 minutes long."
          );
          setFileError(true); // Update your state to reflect the error
          setFileTypeErrorMessage(
            "Video duration must be between 1 second and 20 minutes."
          );
          // Alert the user or handle the error as needed
        } else {
          console.log("About to prepare video preview and show modal.");
          // If all checks pass, set the file for upload
          setFileUpload(file);
          prepareVideoPreviewAndShowModal(file); // This should correctly prepare and trigger the modal
          setDurationError("");
          setFileError(false);
        }
      });
      // In case there's an error loading the metadata
      videoElement.addEventListener("error", (event) => {
        console.error("Error loading video metadata:", event);
        setFileError(true); // Update your state to reflect the error
      });
    } catch (error) {
      setFileTypeErrorMessage(error.message); // Pass the error message to display
      setFileError(true); // Update your state to reflect the error
    }
  };

  // Function to prepare and show the modal with the image preview
  const prepareVideoPreviewAndShowModal = (file) => {
    const previewVideoUrl = URL.createObjectURL(file);
    setVideoPreviewUrl(previewVideoUrl);
    console.log(`Video Preview URL set: ${previewVideoUrl}`);
    setPreviewVideoModal(true);
    console.log(`Preview Video Modal set to true`);
  };

  const handleModalClose = () => {
    // Check if there's a preview URL to revoke
    if (videoPreviewUrl) {
      URL.revokeObjectURL(videoPreviewUrl);
      setVideoPreviewUrl(null); // Clear the preview URL state
    }
    setPreviewVideoModal(false); // Close the modal
  };
  const doesUserHavePermissions = () => {
    const isProfileComplete =
      !!currentMemberData?.firstName &&
      !!currentMemberData?.lastName &&
      !!currentMemberData?.email;

    const isPhoneVerified = !!currentMemberData?.phoneNumberVerified;

    if (!isProfileComplete || !isPhoneVerified) {
      setUserHasPermissions(false);
      setShowError(true);
      return false;
    }

    setUserHasPermissions(true);
    return true;
  };
  const handleSubmitUpload = async () => {
    // Check permissions first
    if (!doesUserHavePermissions()) {
      console.warn("Upload blocked: profile incomplete or phone not verified");
      return;
    }
    setTitleError(false);
    setFileError(false);
    setCategoryErr(false);
    setBackendErrorMessage(""); // Reset error message state
    let hasError = false;
    // Direct checks for errors
    if (!titleValue || titleValue.trim() === "") {
      console.error("Title validation failed");
      setTitleError(true);
      hasError = true;
    }
    if (!fileUpload) {
      console.error("File validation failed");
      setFileTypeErrorMessage("Please select a valid video file to upload.");
      setFileError(true);
      hasError = true;
    }
    if (!selectedCategory.id) {
      console.error("Category validation failed");
      setCategoryErr(true);
      hasError = true;
    }
    if (hasError) {
      console.log("Validation failed, aborting upload");
      return;
    }
    setLoading(true); // Set loading to true before starting the upload process
    // Construct formData for backend notification
    const skitId = uuidv4();
    const formData = {
      title: titleValue,
      categoryid: selectedCategory.id,
      sourceId: currentMemberId,
      videoMetaData: videoMetadata,
      fileName: fileUpload.name,
      skitId, // Include the generated skitId
    };
    console.log("Form data constructed:", formData);
    localStorage.setItem("formData", JSON.stringify(formData));
    // Checking if currentMemberId exists
    if (!currentMemberId) {
      console.error(
        "No current user. Please sign in or sign up to upload files."
      );
      setLoading(false); // Set loading to false if no current user
      // You can add logic here to prompt the user to sign in, if they're not already
      // This could include showing a message, redirecting to a sign-in page, etc.
      return; // Abort the upload process if no current user is found
    }
    try {
      console.log("Requesting pre-signed URL for:", {
        fileName: fileUpload.name,
        mimeType: fileUpload.type,
        fileSize: fileUpload.size,
        currentMemberId, // This is the sourceId for the Skit
        skitId,
      });
      // Collecting File Details: then make a call to your backend endpoint to request a pre-signed URL, passing along these file details.
      // see function for how it is done on backend by clicking on the getPresigedVideoUrl
      const presignedVideoUrlResponse = await getPresignedVideoUrl(
        fileUpload.name,
        fileUpload.type,
        fileUpload.size,
        currentMemberId, // Pass the currentMemberId as the sourceId
        skitId // Pass the generated skitId here
      );
      if (presignedVideoUrlResponse.presignedVideoUrl) {
        // If the pre-signed URL is present, consider it a success
        console.log(
          "Pre-signed URL obtained, URL:",
          presignedVideoUrlResponse.presignedVideoUrl
        );
        setPresignedVideoUrlResponse(presignedVideoUrlResponse);
      } else {
        console.error("Request for pre-signed URL failed");
        setFileError("Failed to generate an upload URL. Please try again.");
        setLoading(false); // Set loading to false if the request fails
      }
    } catch (err) {
      console.error("Error during file upload process:", err);
      if (err.response && err.response.data && err.response.data.message) {
        setBackendErrorMessage(err.response.data.message); // Set error message from the backend
      } else {
        setBackendErrorMessage("An error occurred during the upload process."); // Fallback error message
      }
      setLoading(false);
    }
  };
  useEffect(() => {}, [currentMemberId]);
  useEffect(() => {
    if (videoRef.current) {
      const handleTimeUpdate = () => {
        setDuration(videoRef.current.duration || 0);
      };

      videoRef.current.addEventListener("timeupdate", handleTimeUpdate);

      return () => {
        if (videoRef.current) {
          videoRef.current.removeEventListener("timeupdate", handleTimeUpdate);
        }
      };
    }
  }, [videoRef]);

  // Using the Pre-signed URL for Upload: If the response contains the pre-signed URL and indicates
  // success, you proceed to use this URL to directly upload the file to the S3 bucket:
  // The actual upload is handled in a separate step, where you make a PUT request to the
  // pre-signed URL with the file content as the request body.
  useEffect(() => {
    console.log("useEffect triggered", {
      presignedVideoUrlResponse,
      fileUpload,
    });
    if (presignedVideoUrlResponse?.presignedVideoUrl && fileUpload) {
      // this response will invoke the following below
      console.log(
        "Attempting upload with URL:",
        presignedVideoUrlResponse.presignedVideoUrl
      );
      console.log("Uploading file details:", {
        URL: presignedVideoUrlResponse.presignedVideoUrl,
        "File MIME type": fileUpload.type,
        File: fileUpload,
      });
      // using the file object directly
      uploadFileToS3(
        presignedVideoUrlResponse.presignedVideoUrl,
        fileUpload, // this is the actual file object that needs to be uploaded
        handleUploadSuccess, // Direct reference, not invoking here instead of a successs response message commented out below
        // () => console.log("Upload Success")
        (error) => console.error("Upload Error:", error)
      );
    }
  }, [presignedVideoUrlResponse, fileUpload]);

  const handleUploadSuccess = async () => {
    const formDataString = localStorage.getItem("formData");
    if (!formDataString) {
      console.error("No skit data found in local storage.");
      return;
    }
    const skitData = JSON.parse(formDataString);
    skitData.loggedInAccountHolderId = skitData.sourceId; 
    const cloudFrontDomain = "media.skitsicon.com";
    skitData.mediaUrl = `https://${cloudFrontDomain}/videos/${skitData.sourceId}/${skitData.skitId}/${encodeURIComponent(
      skitData.fileName
    )}`;
    delete skitData.videoMetaData;

    try {
      const response = await apiRecordVideoDataDatabase(skitData);

      const ok =
        (response?.status === 201 && response?.data && response?.data.id) ||
        response?.data?.success === true;
      if (ok) {
        // cleanup
        localStorage.removeItem("formData");
        setTitleValue("");
        setSelectedCategory({ id: "", name: "" });
        setVideoMetadata({ duration: 0, width: 0, height: 0 });
        setFileUpload(null);
        // notify parent
        onServerSuccess?.(response.data);
        return;
      }
      console.error("Failed to record skit data:", response?.data);
    } catch (error) {
      console.error("Error recording skit data:", error);
    } finally {
      setLoading(false); // ensure spinner stops
    }
  };

  //   const handleUploadSuccess = async () => {
  //     // Retrieve skit details from local storage
  //     const formDataString = localStorage.getItem("formData");
  //     if (formDataString) {
  //       let skitData = JSON.parse(formDataString);
  //       // Optionally include the presignedVideoUrl in your skitData
  //       const cloudFrontDomain = "media.skitsicon.com"; // Your CloudFront domain
  //       skitData.mediaUrl = `https://${cloudFrontDomain}/videos/${skitData.sourceId}/${skitData.skitId}/${encodeURIComponent(skitData.fileName)}`;
  //       // Optionally remove unnecessary fields from skitData if they exist
  //       delete skitData.videoMetaData; // Assuming you want to remove this before sending
  //       // Log the constructed CloudFront URL for verification
  //       console.log("Constructed CloudFront URL:", skitData.mediaUrl);
  //       // Invoke the API function to notify the backend of upload completion
  //       try {
  //         console.log(
  //           "Invoking recordDataInDatabase with skit data from local storage"
  //         );
  //         const response = await apiRecordVideoDataDatabase(skitData);
  //        // Normalize “success” (201 w/ entity OR { success: true }):
  //         const ok =
  //            (response?.status === 201 && response?.data && response?.data.id) ||
  //            response?.data?.success === true;
  //             if (ok) {
  //           // Only clear local storage upon successful backend response
  //           // Clear the form data from local storage and state upon successful upload
  //           localStorage.removeItem("formData");
  //           setTitleValue("");
  //           setSelectedCategory({ id: "", name: "" }); // Reset to initial category state
  //           setVideoMetadata({ width: 0, height: 0, size: 0 }); // Reset image metadata
  //           setFileUpload(null); // Clear the uploaded file

  //     // 👉 emit domain-level success so the parent/app decides policy
  //   onServerSuccess?.(response.data);

  //       return;
  //     }
  //     console.error("Failed to record skit data:", response?.data);
  //   } catch (error) {
  //     console.error("Error recording skit data:", error);
  //   }
  // };
  const handleTitleChange = (event) => {
    let newTitle = event.target.value;
    let maxTitleLength = 50;
    // If user typed beyond the limit, slice it back
    // since my array has 50 characters then slicing it from 0 to 49 pushes the user back
    // so now the user can't type beyond the max data validation allowance
    if (newTitle.length > maxTitleLength) {
      newTitle = newTitle.slice(0, maxTitleLength);
    }
    // Update your local state with the (possibly sliced) title
    setTitleValue(newTitle);
    // "Required" error if empty or whitespace
    if (newTitle.trim() !== "") {
      setTitleError(false);
    } else {
      setTitleError(true);
    }
    // show an error message when at the limit
    if (newTitle.length === maxTitleLength) {
      setTitleLengthError(`Maximum ${maxTitleLength} characters reached.`);
    } else {
      setTitleLengthError(false);
    }
  };
  // guard manual category changes when locked
  const handleCategorySelect = (categoryName, categoryId) => {
    if (isCategoryLocked) return;
    setSelectedCategory({ name: categoryName, id: categoryId });
    setCategoryErr(false);
  };

  const handleDragUpdate = (time) => {
    if (videoRef.current) {
      videoRef.current.currentTime = time; // Sync video position with drag
    }
  };

  useEffect(() => {
    console.log("Current selected category state:", selectedCategory);
  }, [selectedCategory]);

  const uploadSkitActionBtns = () => {
    if (fileUpload) {
      // switch the buttons on the frontend if we collect a file
      return (
        <div className="button-container">
          <SquareFlatBtnsUpload
            name="changeSkitButton"
            onClick={triggerFileInputClick} // Click the hidden file input to change the skit
            Icon={SearchGlassIcon} // Use the same style button
            iconWidth="20px"
            iconHeight="20px"
            title="CHANGE"
            SqaureBtnbackgroundColor="#1c1c1c" // Button background color
            extraStyles={{
              marginLeft: "20px", // Adjust margin as needed to position the button
              padding: "10px",
            }} // Any additional styles you want to apply
            titleStyle={{
              color: "var(--color-bloodred)",
              fontFamily: "Roboto Slab",
              fontWeight: "800",
              fontSize: ".75rem",
            }} // Styling for the button text
            border="0.2px solid #c79f27" // Button border properties
            borderRadius="4px" // Making the button square with rounded corners
            boxShadow="0 0 10px 0px rgba(0, 0, 0, 0.5)" // Box shadow for the button
            transition="background-color 1s cubic-bezier(0.075, 0.82, 0.165, 1)" // Transition effect for the button
          />
          {loading && <Loader />}
          <SquareFlatBtnsUpload
            name="competeButton"
            onClick={handleSubmitUpload} // Change the handler to submit the form
            Icon={BlinkingSidewaysRightTriangleIcon} // Pass the component, not an instance
            iconWidth="20px"
            iconHeight="20px"
            title="BROADCAST"
            SqaureBtnbackgroundColor="#1c1c1c" // Button background color
            extraStyles={{
              marginLeft: "100px",
              padding: "10px",
            }} // Any additional styles you want to apply
            titleStyle={{
              color: "var(--color-bloodred)",
              fontFamily: "Roboto Slab",
              fontWeight: "800",
              fontSize: ".75rem",
            }} // Styling for the button text
            border="0.2px solid #c79f27" // Button border properties
            borderRadius="4px" // Making the button square with rounded corners
            boxShadow="0 0 10px 0px rgba(0, 0, 0, 0.5)" // Box shadow for the button
            transition="background-color 1s cubic-bezier(0.075, 0.82, 0.165, 1)" // Transition effect for the button
          />
        </div>
      );
    } else {
      // If no video has been selected, show "CHOOSE VIDEO"
      return (
        <div>
          {fileError && (
            <div className="campiagn-file-error-message">
              {fileTypeErrorMessage}
            </div>
          )}
          <InsetBlendTVButton
            image={<SearchGlassIcon />}
            btnText="CHOOSE SKIT"
            textStyles={{
              color: "var(--color-bloodred)",
              fontFamily: "Roboto Slab",
              fontWeight: "800",
              fontSize: "1.3rem",
            }}
            style={{
              width: "250px",
            }}
            onClick={triggerFileInputClick} // Keep the original handler to trigger file selection
          />
        </div>
      );
    }
  };
  const skitCategoryButtons = (categoriesToRender) => {
    return (
      <>
        {categoriesToRender.map((category) => (
          <SquareFlatBtnsUpload
            key={category.id}
            name={category.name}
            Icon={category.Icon}
            title={category.name}
            className={`upload-remote-category-choice-btn ${category.name.toLowerCase()}-btn`}
            extraStyles={{
              minWidth: "80px", // keeps small ones from collapsing
              padding: "2px 4px", // extra breathing space
            }}
            iconWidth="30px"
            iconHeight="30px"
            onClick={() => handleCategorySelect(category.name, category.id)}
            titleStyle={{
              fontSize: ".65rem",
              fontWeight: "800",
              color:
                selectedCategory.id === category.id
                  ? "var(--color-ivory)"
                  : "var(--color-gold)",
            }}
          />
        ))}
      </>
    );
  };

  return (
    <div
      className={`upload-video-modal ${isUploadVideoFormOpen ? "is-open" : "is-closed"}`}
    >
      <div
        className="upload-video-modal-content"
        onScroll={(e) => e.stopPropagation()} // Prevents scroll event propagation inside the modal
        ref={modalRef}
      >
        <SquareFlatBtnsUpload
          name="cancelButton"
          className="cancel--upload-skit-btn" // <-- Additional class for cancel button alignment
          onClick={closeModal}
          Icon={VerticalTwoBarsIcon}
          iconWidth="10px"
          title="CANCEL"
          extraStyles={{
            paddingTop: "1px",
          }}
          titleStyle={{
            color: "var(--color-bloodred)",
            fontFamily: "Roboto Slab",
            fontWeight: "800",
            fontSize: ".75rem",
          }}
          buttonWidth="65px"
          border="0.2px solid #c79f27"
          borderRadius="4px"
          boxShadow="0 0 10px 0px rgba(0, 0, 0, 0.5)"
          transition="background-color 1s cubic-bezier(0.075, 0.82, 0.165, 1)"
        />

        {BackendErrorMessage && (
          <div className="backend-uploadSkit-error-message">
            {BackendErrorMessage}
          </div>
        )}

        {/* content switch */}
        {step === STEPS.COMPLIANCE ? (
          <div className="preflight-card">
            <h2>
              <span aria-hidden>🎬</span> Before You Upload
            </h2>

            <p>Check any of the following if your skit includes?</p>
            <label>
              <input
                type="checkbox"
                checked={flags.puppets}
                onChange={() =>
                  setFlags((f) => ({ ...f, puppets: !f.puppets }))
                }
              />{" "}
              Puppets
            </label>
            <label>
              <input
                type="checkbox"
                checked={flags.cartoons}
                onChange={() =>
                  setFlags((f) => ({ ...f, cartoons: !f.cartoons }))
                }
              />{" "}
              Cartoons
            </label>
            <label>
              <input
                type="checkbox"
                checked={flags.ai}
                onChange={() => setFlags((f) => ({ ...f, ai: !f.ai }))}
              />{" "}
              AI-generated scenes/characters
            </label>
            <label>
              <input
                type="checkbox"
                checked={flags.vfx}
                onChange={() => setFlags((f) => ({ ...f, vfx: !f.vfx }))}
              />{" "}
              Stop-motion / VFX / other animation
            </label>

            {isAnimation && (
              <div className="preflight-hint">
                ✅ We’ll set <b>Animations</b> and lock it.
              </div>
            )}

            <div className="preflight-rules">
              <ul className="rules">
                <li>
                  Submit each skit in <b>one</b> category per week.
                </li>
                <li>
                  A skit can only win in <b>one</b> category.
                </li>
                <li>Winning skits can’t be re-submitted later.</li>
                {/* warning row */}
                <li className="is-warning">
                  ⚠️ Re-submitting the same skit across categories = removal.
                </li>
                {/* green-light row */}
                <li>
                  <TrafficLightIcon
                    active="green"
                    size={14}
                    style={{ marginRight: 6 }}
                  />
                  If your skit didn’t win, you may re-submit it in a{" "}
                  <b>future week</b> (still one category per week).
                </li>
              </ul>
            </div>
            <label className="agree-row">
              <input
                type="checkbox"
                checked={agree}
                onChange={(e) => setAgree(e.target.checked)}
              />
              I agree to the rules.
            </label>

            <div className="preflight-actions">
              <button disabled={!agree} onClick={continueFromCompliance}>
                Continue
              </button>
            </div>
          </div>
        ) : (
          // === UPLOAD BODY (your existing UI) ===
          <>
            <div className="upload-info-message">
              For the best viewing experience, please upload videos in
              landscape...
            </div>

            <div className={`form-group ${titleError ? "error-border" : ""}`}>
              <h3>Skit Title:</h3>
              {titleLengthError && (
                <div className="error-message">{titleLengthError}</div>
              )}
              <div className="upload-input-container blinker-wrapper">
                <input
                  id="videoTitle"
                  type="text"
                  value={titleValue}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  onChange={handleTitleChange}
                  placeholder={
                    isFocused || titleValue
                      ? ""
                      : titleError
                        ? "Required"
                        : "Type video title here"
                  }
                  className={titleError ? "red-placeholder" : ""}
                />
                {!isFocused && titleValue === "" && (
                  <span className="blinking-cursor-gold video-title-cursor">
                    |
                  </span>
                )}
              </div>
            </div>
            {showError && (
              <ErrorMessageModal
                message="You must complete your profile and verify your phone number before uploading a skit."
                onClose={() => setShowError(false)}
              />
            )}
            <div className={`form-group ${fileError ? "error-border" : ""}`}>
              <input
                type="file"
                name="my-video"
                accept="video/*"
                style={{ display: "none" }}
                ref={fileInputRef}
                onChange={handleUploadVideo}
              />
              <h4>Choose a Category:</h4>
              {isCategoryLocked ? (
                <div className="locked-category">
                  🔒 Category locked to{" "}
                  <b>{selectedCategory.name || "Animations"}</b>
                </div>
              ) : (
                <div
                  className={`categories-container ${categoryErr ? "error-border" : ""}`}
                >
                  {skitCategoryButtons(memberCategoriesChoice)}
                </div>
              )}

              {uploadSkitActionBtns()}
              <div>
                {videoMetadata.duration > 0 && (
                  <div className="video-details-container">
                    <h3>Video Details:</h3>
                    <p>Duration: {formatDuration(videoMetadata.duration)}</p>
                    {/* <p>Duration: {videoMetadata.duration.toFixed(2)} seconds</p> */}
                    {/* <p>
                  Resolution: {videoMetadata.width} x {videoMetadata.height}
                </p> */}
                    {durationError && (
                      <div className="error-message">{durationError}</div>
                    )}
                  </div>
                )}
              </div>
              {previewVideoModal && (
                <div className="video-preview-container">
                  <VideoMonitor
                    videoRef={videoRef}
                    src={videoPreviewUrl}
                    title="Video Preview"
                    autoPlay={false}
                    onLoadedMetadata={() => {
                      if (videoRef.current) {
                        setDuration(videoRef.current.duration || 0); // Update duration when metadata loads
                      }
                    }}
                  />
                  <div id="controlsAndActionsRow">
                    <VideoPlayerControls
                      videoRef={videoRef}
                      duration={duration}
                      volume={volume}
                      setVolume={setVolume}
                      onDragUpdate={handleDragUpdate} // Handle drag updates
                      isSkitPlaying={isSkitPlaying} // Pass down the state
                      setIsSkitPlaying={setIsSkitPlaying} // Allow child to update the state
                      showVolumeControl={false} // Hide volume control here
                    />
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
export default UploadVideoForm;
